import { useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import { Badge, Field, Select } from './ui'
import { useModels } from '@/context/ModelsContext'

/**
 * 模型选择器（全站统一）。
 *
 * 只渲染**真实可选**的模型，且一个上游模型只出现一条——
 * 「文生图 / 图生图」不是两个模型，提交时由表单里有没有参考图自动决定，
 * 所以下拉里看不到第二项，也不会出现「官方 / 渠道 / 渠道编辑」这种同义重复。
 *
 * ## 只有下拉一种形态（2026-09-29 定）
 *
 * 2026-09-28 曾给视频页加过第二形态 `variant="cards"`（竖排引擎卡，单价印在右侧）。
 * 次日用户驳回，原话「下拉框就可以了，现在太占位置」—— 实测那一块在 1482px 宽的
 * 窗口里占 **1205px 高**（16 个模型 × 68px 卡片），把参考素材与提交栏全顶到折线以下。
 * **没有第二个消费者就不留第二形态**：死分支 + 与之配套的 CSS 与注释都是负债
 * （见 MEMORY.md 铁律「交互控件只许一份实现」，以及 §4.4-25「下架能力后 grep 基元还有没有人用」）。
 *
 * 撤掉卡片不等于丢信息，三条都还在：
 *   · 单价 —— 写进每个 option 的文本（`阿里云 Wan 3.0 · ¥0.80/秒 · 已实测`）；
 *   · 通道 —— 字段名右侧的徽标（duomi 直连 / 贰春开放 API / songshuai 网关）。同一
 *     上游挂两个渠道时，两个名字本来就不一样，下拉里分得清；
 *   · 备注与提交形态 —— 控件下方的 hint，跟着 `kind` 说本页的规则。
 */
export function ModelSelect({
  kind,
  value,
  onChange,
  hasRef = false,
  label = '生成模型',
}: {
  kind: 'image' | 'video'
  /** family id */
  value: string
  onChange: (familyId: string) => void
  /** 表单里是否已有参考素材——只影响下方的提示文案，不影响选项 */
  hasRef?: boolean
  label?: string
}) {
  const { list, source, error } = useModels()
  const options = useMemo(() => list(kind), [list, kind])

  /*
    「已实测 / 未实测」只在**有对比**时才有信息量。

    2026-10-09 起两档都是混的：视频档 17 条里只有 3 条实测过（贰春 `wan-3-0` /
    `sd20-channel` + 速创 `google-omni`），其余 14 条贰春档是**目录里有、本项
    目没跑过**；图片档 5 条里 2 条实测。有对比就得如实标 —— 用户挑引擎的唯一
    依据就是它。反过来，一处全挂同一个词的清单（比如早先「只留已实测」时的视频档）
    等于把那个词重复十几遍、还占掉每行宽度，所以 `mixedVerified` 为 false 时
    **一个后缀都不加**。
  */
  const mixedVerified = options.some((o) => !o.verified)

  const current = options.find((o) => o.id === value) ?? options[0] ?? null

  // 草稿里可能残留旧 slug / 已下线的模型 → 回落到第一个可用项并回写
  useEffect(() => {
    if (current && current.id !== value) onChange(current.id)
  }, [current, value, onChange])

  if (!options.length) {
    return (
      <Field label={label} hint={error ?? undefined}>
        <Select disabled value="">
          <option value="">暂无可用模型</option>
        </Select>
      </Field>
    )
  }

  const sourceHint =
    source !== 'remote' ? (
      <span style={{ opacity: 0.75 }}>
        {source === 'cache' ? '模型清单来自缓存（后端未连上，正在重试）' : '模型清单为内置兜底，后端未连上'}
        {error ? `：${error}` : ''}
      </span>
    ) : null

  /**
   * 「这次会走哪种提交」那行提示 —— **文案跟着 `kind` 走**。
   *
   * 以前无论哪一页都写「文生图 / 图生图」。视频页上这句是**另一套规则的文案**：
   * 说的是这页不存在的动作（更早的卡片形态写的是对的那句，撤卡片时差点把它一起丢掉）。
   */
  const submitHint = hasRef
    ? kind === 'video'
      ? '已传参考素材，将按图生视频提交'
      : '已传参考图，将按图生图提交'
    : kind === 'video'
      ? '文生视频；传了参考素材会自动切图生视频'
      : '文生图；传了参考图会自动切图生图'

  /**
   * 引擎说明 —— **只有「异常」与「前提」才留**（2026-10-09 删繁）。
   *
   * 用户口径「选择引擎的说明删除」。删掉的是三类**说明性**文字：
   *   · `¥1.00/次 · ¥1/次 · 720p · …` —— 单价与「已实测 / 未实测」已经印在每个
   *     option 的文本里（见文件头注，以及下面铺 option 时的后缀逻辑），这一行是
   *     **同一句话的第二遍**：截图里「¥1.00/次」连着出现两次，正是用户圈出来的啰嗦。
   *   · 「文生视频；传了参考素材会自动切图生视频」—— 自动行为不必事先预告。
   *   · 「只列已实测跑通的模型…」—— 视频档不再按「已实测」筛（贰春只按用户点名的
   *     白名单上架、允许未实测档），这句话本身就不成立了；「实测与否」改由每个
   *     option 的后缀逐条如实说，不需要再加一行总述。
   *
   * 留下的两条都不是「说明」：
   *   · 清单降级（后端没连上 / 走缓存）—— 不说用户会以为模型真的只剩两条；
   *   · 走网关通道需网关余额 —— 余额不足时提交会失败，这是**后果**不是解释。
   *
   * ⚠️ **只对 `kind === 'video'` 收口**。图片档那条 `note` 里装着出图规格
   * （分辨率 / 尺寸档），而它是**只在那一行长着**的信息 —— 删了就没有别处可看。
   * 视频档的清晰度与时长由「输出参数」页按模型能力渲染，那边有一份真的。
   */
  const compact = kind === 'video'
  const hintLines: ReactNode[] = []
  if (!compact) {
    hintLines.push(
      <span key="price">
        {current?.unitPrice ? `¥${current.unitPrice.toFixed(2)}/${current.unit}` : ''}
        {current?.note ? ` · ${current.note}` : ''}
      </span>,
    )
    if (current && !current.img2imgSameAsText) {
      hintLines.push(
        <span key="submit" style={{ opacity: 0.75 }}>
          {submitHint}
        </span>,
      )
    }
  }
  if (current?.channel === 'gateway') {
    hintLines.push(
      <span key="gw" style={{ opacity: 0.75 }}>
        走网关通道，需网关余额（duomi 直连不需要）
      </span>,
    )
  }
  if (sourceHint) hintLines.push(<span key="src">{sourceHint}</span>)

  return (
    <Field
      label={label}
      right={
        current?.channel ? (
          <Badge tone={current.channel === 'duomi' ? 'success' : 'warning'}>
            {current.channelLabel}
          </Badge>
        ) : null
      }
      /* 一行都没有时**整个 hint 不传** —— 留一个空 `<span>` 会在字段下方
         撑出一条无名的空隙，看着像漏渲染。 */
      hint={hintLines.length ? <span style={{ display: 'grid', gap: 4 }}>{hintLines}</span> : undefined}
    >
      <Select value={current?.id ?? ''} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
            {o.unitPrice ? ` · ¥${o.unitPrice.toFixed(2)}/${o.unit}` : ''}
            {mixedVerified ? (o.verified ? ' · 已实测' : ' · 未实测') : ''}
          </option>
        ))}
      </Select>
    </Field>
  )
}
